import React, { useEffect, useState, useRef } from 'react';
import '../../styles/Home/upcoming.css';
import { fetchTripsByCategory } from '../../functions/FetchTrips';
import { useNavigate } from 'react-router-dom';

// Helper to format "June 2025" or "YYYY-MM" as "Jun '25"
const formatMonthLabel = (monthStr) => {
    if (!monthStr) return '';
    // Try "June 2025" format
    const [month, year] = monthStr.split(" ");
    if (month && year && !isNaN(Number(year))) {
        const date = new Date(`${month} 1, ${year}`);
        if (!isNaN(date)) {
            const shortMonth = date.toLocaleString('en-US', { month: 'short' });
            const shortYear = String(date.getFullYear()).slice(-2);
            return `${shortMonth} '${shortYear}`;
        }
    }
    // Try "YYYY-MM" format
    if (/^\d{4}-\d{2}$/.test(monthStr)) {
        const [y, m] = monthStr.split("-");
        const date = new Date(Number(y), Number(m) - 1, 1);
        if (!isNaN(date)) {
            const shortMonth = date.toLocaleString('en-US', { month: 'short' });
            const shortYear = String(date.getFullYear()).slice(-2);
            return `${shortMonth} '${shortYear}`;
        }
    }
    return monthStr;
};

const UpcomingTrips = ({ hideControls = false, trips: propTrips, loading: propLoading, error: propError }) => {
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [scrollIndex, setScrollIndex] = useState(0);
    const cardsPerPage = 8;
    const gridRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (propTrips) {
            setTrips(propTrips);
            setLoading(!!propLoading);
            setError(propError || null);
            return;
        }
        const fetchUpcomingTrips = async () => {
            setLoading(true);
            try {
                const upcomingTrips = await fetchTripsByCategory("upcoming");
                setTrips(upcomingTrips);
                setError(null);
            } catch (err) {
                setError('Failed to load trips. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchUpcomingTrips();
    }, [propTrips, propLoading, propError]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Generate next 12 months starting from current month (YYYY-MM)
    const monthOptions = React.useMemo(() => {
        if (hideControls) return []; // Don't generate months if controls are hidden
        const options = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const val = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            options.push(val);
        }
        return options;
    }, [hideControls]);

    // Set default selectedMonth to current month if not set
    useEffect(() => {
        if (!hideControls && monthOptions.length > 0 && !selectedMonth) {
            setSelectedMonth(monthOptions[0]);
        }
    }, [monthOptions, selectedMonth, hideControls]);

    // Filter trips by selected batch month (skip filtering if hideControls)
    const filteredTrips = hideControls
        ? trips.filter(trip => trip.status !== "Draft")
        : selectedMonth
            ? trips
                .filter(trip => trip.status !== "Draft")
                .filter(trip => {
                    if (!trip.batches || typeof trip.batches !== "object") return false;
                    // Check if any batch key matches the selectedMonth
                    return Object.keys(trip.batches).some(batchKey => {
                        // batchKey: "June 2025"
                        const [month, year] = batchKey.split(" ");
                        if (!month || !year) return false;
                        const date = new Date(`${month} 1, ${year}`);
                        if (isNaN(date)) return false;
                        const batchVal = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                        return batchVal === selectedMonth && Array.isArray(trip.batches[batchKey]) && trip.batches[batchKey].length > 0;
                    });
                })
            : [];

    // Scroll handlers
    const handleScrollLeft = () => {
        setScrollIndex((prev) => Math.max(prev - cardsPerPage, 0));
    };
    const handleScrollRight = () => {
        setScrollIndex((prev) =>
            Math.min(prev + cardsPerPage, Math.max(filteredTrips.length - cardsPerPage, 0))
        );
    };

    // Slice the trips to show only the current "page"
    const visibleTrips =
        filteredTrips.length > cardsPerPage
            ? filteredTrips.slice(scrollIndex, scrollIndex + cardsPerPage)
            : filteredTrips;

    const renderTripCard = (trip) => {
        // Extract all numeric prices from starting_price object
        let prices = [];
        if (trip.starting_price && typeof trip.starting_price === "object") {
            prices = Object.values(trip.starting_price)
                .map(val => Number(val))
                .filter(val => !isNaN(val));
        }
        const minPrice = prices.length > 0 ? Math.min(...prices) : "";
        const oldPrice = minPrice ? minPrice + 3000 : "";

        // Format price as INR with commas
        const formatINR = (amount) =>
            amount ? `₹${amount.toLocaleString("en-IN")}` : "";

        return (
            <div className="col-6 col-md-3" key={trip.id}>
                <div
                    className="upctrip-trip-card"
                    onClick={() => navigate(`/itinerary/${trip.id}`)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="upctrip-price-tag">
                        {minPrice ? (
                            <>
                                <span className="upctrip-old-price">
                                    {oldPrice ? `${formatINR(oldPrice)}` : ""}
                                </span>
                                {' '}
                                <span className="upctrip-current-price">
                                    {`Starting from ${formatINR(minPrice)}`}
                                </span>
                            </>
                        ) : (
                            <span className="upctrip-current-price">Contact for price</span>
                        )}
                    </div>
                    {/* Only render img if imageUrl is non-empty */}
                    {(() => {
                        const imageUrl = trip.image || trip.cover_image_url;
                        if (imageUrl && imageUrl.trim() !== "") {
                            return (
                                <img
                                    src={imageUrl}
                                    alt={trip.title || trip.tripname}
                                    className="upctrip-trip-img"
                                />
                            );
                        }
                        return null;
                    })()}
                    {/* <div className="upctrip-trip-content">
                            <div className="upctrip-trip-title">{trip.title || trip.tripname}</div>
                            <div className="upctrip-trip-info d-flex justify-content-between">
                                <div className="upctrip-duration">
                                    <i className="far fa-clock"></i>
                                    <span>
                                        <b>
                                            {trip.days ? `${trip.days}D` : ""}{trip.nights ? `/${trip.nights}N` : ""}
                                        </b>
                                    </span>
                                </div>
                            </div>
                            <div className="upctrip-trip-info upctrip-trip-location">
                                <i className="fas fa-map-marker-alt"></i>
                                <span><b>{trip.location}</b></span>
                            </div>
                        </div> */}
                </div>
            </div>
        );
    };

    return (
        <div className="upctrip-container" id="upcoming-trips">
            <div className={`upctrip-header-section ${isMobile ? 'text-center' : ''}`}>
                <h1>
                    {hideControls ? 'Explore Our Trips' : 'Upcoming Trips'}
                </h1>
            </div>

            {/* Month filter tabs */}
            {!hideControls && (
                <div className="upctrip-month-tabs upctrip-month-tabs-scroll">
                    {monthOptions.map((val) => (
                        <div
                            key={val}
                            className={`upctrip-month-tab${selectedMonth === val ? ' active' : ''}`}
                            onClick={() => setSelectedMonth(val)}
                        >
                            {formatMonthLabel(val)}
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading trips...</p>
                </div>
            ) : error ? (
                <div className="alert alert-danger text-center my-4" role="alert">
                    {error}
                </div>
            ) : filteredTrips.length === 0 ? (
                <div className="alert alert-info text-center my-4" role="alert">
                    No trips available for this month. Please check back later.
                </div>
            ) : (
                <div className="upctrip-mobile-trips-grid position-relative">
                    {filteredTrips.length > cardsPerPage && (
                        <>
                            <button
                                className="upctrip-scroll-btn left"
                                onClick={handleScrollLeft}
                                disabled={scrollIndex === 0}
                                aria-label="Scroll left"
                            >
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            <button
                                className="upctrip-scroll-btn right"
                                onClick={handleScrollRight}
                                disabled={scrollIndex + cardsPerPage >= filteredTrips.length}
                                aria-label="Scroll right"
                            >
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </>
                    )}
                    <div className="row g-3" ref={gridRef}>
                        {visibleTrips.map(trip => renderTripCard(trip))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UpcomingTrips;
